import { useEffect, useRef } from "react";
import { useAuth, useTokens, useStorageManager } from "../../shared/hooks";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CONSTANT } from "../../shared/constants/constant-data";
import { useDispatch, useSelector } from "react-redux";
import { setMyScanSocketData, setMyScanStatusList, setReportDetails } from "../../pages/onPremises/myScan/MyScanTable_Details_slice";
import { showNotification } from '../notifications/NotificationManager';
import { setNotifications, setComplianceTaskData } from './event-manager.slice'
import { connectSocket, closeSocketConnection } from "./socketManager";
import { selectList } from "../../store/refreshActionSlice";
import { NOTIFICATION_REASONS, getNotificationMessage } from "../../axios/constants";
import eventEmitter from "../utils/eventEmitter";
import ConfirmationToast from "../ConfirmationToast";


const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET;
const DEVICE_CODE = import.meta.env.VITE_WEB_SOCKET_DEVICE_KEY;

const isMasterTemplateEvent = (value: any): boolean =>
  ["master_template_processed", "master_templates_processed"].includes(
    String(value || "").toLowerCase()
  );

const findMasterTemplatePayload = (value: any, depth = 0): any | null => {
  if (!value || depth > 6) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const payload = findMasterTemplatePayload(item, depth + 1);
      if (payload) return payload;
    }
    return null;
  }

  if (typeof value !== "object") return null;

  const eventName = value.event || value.action || value.type;
  if (isMasterTemplateEvent(eventName)) {
    const eventData = value.data;
    if (eventData && typeof eventData === "object") {
      const nestedPayload = findMasterTemplatePayload(eventData, depth + 1);
      if (nestedPayload) return nestedPayload;
    }
    return value.template_id || value.templateId || value.id ? value : null;
  }

  const templateId = value.template_id || value.templateId;
  if (
    templateId &&
    (value.status !== undefined ||
      value.status_label !== undefined ||
      value.status_message !== undefined ||
      value.processed_at !== undefined)
  ) {
    return value;
  }

  for (const child of Object.values(value)) {
    const payload = findMasterTemplatePayload(child, depth + 1);
    if (payload) return payload;
  }

  return null;
};

const normalizeMasterTemplatePayload = (value: any): any | null => {
  const payload = findMasterTemplatePayload(value);
  if (!payload) return null;

  const templateId = payload.template_id || payload.templateId || payload.id;
  if (!templateId) return null;

  const statusMessage = String(payload.status_message || "").toLowerCase();
  const statusLabel =
    payload.status_label ||
    (payload.status === 3 || statusMessage.includes("success") || statusMessage.includes("processed successfully")
      ? "Success"
      : undefined);

  return {
    ...payload,
    template_id: templateId,
    ...(statusLabel ? { status_label: statusLabel } : {}),
  };
};

const EventsManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socketRef = useRef<any>(null);
  const { getToken, clearTokens } = useTokens();
  const { setCookie } = useStorageManager();
  const { isLoggedIn, logout } = useAuth();
  const organization_id = useSelector(
    (state: any) => state.UserLoginDetails.organization_id
  );
  console.log("org id in event manager", organization_id)

  const socketConnection = () => {
    // Clean up any existing connection
    if (socketRef.current) {
      closeSocketConnection();
    }

    // Only establish connection if user is logged in
    if (isLoggedIn) {
      const token = getToken();

      if (token) {
        // Create socket connection with auth token in headers (NOT query params)
        const socketInstance: any = connectSocket(
          `${WEBSOCKET_URL}`,
          {
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 5000,
            timeout: 10000,
            token: token,
            device_code: DEVICE_CODE
          }
        );

        socketRef.current = socketInstance;

        // Handle socket connection
        socketInstance.on("connect", () => {
          console.log("Connected to the WebSocket server");
          if (socketInstance.io && socketInstance.io.opts) {
            socketInstance.io.opts.reconnectionAttempts = 5;
          }
        });

        // Handle connection errors
        // socketInstance.on("connect_error", (error: any) => {
        //   console.error("Socket connection error:", error.message);
        //   if (error.message.includes("Authentication error")) {
        //     clearTokens();
        //     navigate("/login");
        //     toast.error("Authentication failed. Please login again.");
        //   }
        // });

        // Handle expired token event
        socketInstance.on("expire_token", async (response: any) => {
          console.log("in expire token");
          if (response.expire_token === true) {
            clearTokens();
            navigate("/login");
            toast.error(CONSTANT.LOGOUT_DEVICE);
          }
        });

        // Handle socket disconnection
        socketInstance.on("disconnect", (reason: string) => {
          console.log(`Socket disconnected: ${reason}`);
          if (reason || reason === "transport error") {
            console.log("Network issue detected, attempting to reconnect...");
          }
          if (isLoggedIn) reconnectSocket();
        });

        socketInstance.on("custom_message", (reason: any) => {
          console.log("my scans:", reason);
          console.log("custom_message event received:", reason);

          const masterTemplatePayload = normalizeMasterTemplatePayload(reason);
          if (masterTemplatePayload) {
            console.log("Master template status received from custom_message:", masterTemplatePayload);
            eventEmitter.emit("master_template_processed", masterTemplatePayload);
          }

          // Helper to extract and flatten module items from any structure of reason
          const extractModuleData = (dataReason: any, targetModule: string): any[] => {
            if (!dataReason) return [];

            const moduleContainers: any[] = [];
            const checkModuleMatch = (item: any) => {
              if (!item || typeof item !== "object") return false;
              return item.module === targetModule;
            };

            if (Array.isArray(dataReason)) {
              dataReason.forEach((item) => {
                if (checkModuleMatch(item)) {
                  moduleContainers.push(item);
                } else if (Array.isArray(item?.data)) {
                  item.data.forEach((subItem: any) => {
                    if (checkModuleMatch(subItem)) {
                      moduleContainers.push(subItem);
                    }
                  });
                }
              });
            } else if (typeof dataReason === "object") {
              if (checkModuleMatch(dataReason)) {
                moduleContainers.push(dataReason);
              }
              if (Array.isArray(dataReason.data)) {
                dataReason.data.forEach((item: any) => {
                  if (checkModuleMatch(item)) {
                    moduleContainers.push(item);
                  }
                });
              }
            }

            if (moduleContainers.length === 0 && targetModule === "myscans") {
              if (dataReason.action?.startsWith("scan_") || dataReason.scan_uuid || dataReason.scanname) {
                moduleContainers.push(dataReason);
              }
            }

            const itemsList: any[] = [];

            moduleContainers.forEach((container) => {
              const parentAction = container.action;

              const extractItemsFrom = (dataNode: any, activeAction?: string) => {
                if (!dataNode) return;

                if (Array.isArray(dataNode)) {
                  dataNode.forEach((node) => extractItemsFrom(node, activeAction));
                } else if (typeof dataNode === "object") {
                  const currentAction = dataNode.action || activeAction || parentAction;
                  if (Array.isArray(dataNode.data)) {
                    dataNode.data.forEach((inner: any) => extractItemsFrom(inner, currentAction));
                  } else {
                    itemsList.push({
                      ...(currentAction ? { action: currentAction } : {}),
                      ...dataNode,
                    });
                  }
                }
              };

              if (container.data) {
                extractItemsFrom(container.data, parentAction);
              } else if (container.scan_uuid || container.scanname || container.scan_id) {
                itemsList.push(container);
              }
            });

            return itemsList;
          };

          const myScanList = extractModuleData(reason, "myscans");
          console.log("myScanList parsed:", myScanList);

          const myScanStatusList = extractModuleData(reason, "scan_status");
          console.log("myscan status list parsed:", myScanStatusList);

          const assetsListActions = extractModuleData(reason, "asset_management");
          if (assetsListActions.length > 0) {
            dispatch(selectList(assetsListActions));
          }

          const validScanStatusForMainList = myScanStatusList
            .filter((item: any) => item.scan_id || item.scan_uuid || item.id)
            .map((item: any) => ({ ...item, isStatusUpdateOnly: true }));

          dispatch(setMyScanSocketData([...myScanList, ...validScanStatusForMainList]));
          dispatch(setMyScanStatusList(myScanStatusList));
        });



        socketInstance.on('scan_status_event', (response: any) => {
          console.log('[ScanStatusEvent] 🔌 Raw event received:', response);
          console.log('[ScanStatusEvent] file_id:', response?.file_id, '| device_id:', response?.device_id, '| scan_id:', response?.scan_id, '| status:', response?.status, '| reason:', response?.reason);
          // Dispatch scan_status_event data so ScanInformation.tsx can update rows by file_id
          if (response && (response.file_id || response.device_id)) {
            console.log('[ScanStatusEvent] ✅ Dispatching to setMyScanStatusList. Match key:', response.file_id ? `file_id=${response.file_id}` : `device_id=${response.device_id}`);
            dispatch(setMyScanStatusList([response]));
          } else {
            console.warn('[ScanStatusEvent] ⚠️ Skipped dispatch — no file_id or device_id found in event data:', response);
          }
        });

        // Handle sync events (topic-dev and dev-group)
        socketInstance.on('socket-data-sync-topic-dev', (response: any) => {
          console.log('Socket data sync topic-dev:', response);

        });

        socketInstance.on('socket-data-sync-dev-group', (response: any) => {
          console.log('Socket data sync dev-group:', response);
        });

        // Handle my scans status event
        socketInstance.on('final_myscans_status', (response: any) => {
          console.log("response ", response)
        });

        socketInstance.on('master_template_processed', (response: any) => {
          console.log("master_template_processed event received:", response);
          const masterTemplatePayload = normalizeMasterTemplatePayload(response);
          if (masterTemplatePayload) {
            eventEmitter.emit('master_template_processed', masterTemplatePayload);
          }
        });

        socketInstance.on('compliance_task_completed', (response: any) => {
          console.log("compliance_task_completed response ", response)
        });

        socketInstance.on('report_status', (response: any) => {
          console.log('Report status event received:', response);
          console.log('Dispatching setReportDetails with:', response);
          dispatch(setReportDetails(response));
          console.log("Report details dispatched successfully");
        });

        socketInstance.on(organization_id, async (response: any) => {
          console.log("123 event data:", response);

          if (response?.event === 'master_template_processed' || response?.action === 'master_template_processed') {
            console.log("master_template_processed event routed from org topic:", response);
            const masterTemplatePayload = normalizeMasterTemplatePayload(response);
            if (masterTemplatePayload) {
              eventEmitter.emit('master_template_processed', masterTemplatePayload);
            }
          }

          if (response?.action === 'compliance_task_completed') {
            console.log('✅ Compliance task completed action detected!');
            dispatch(setComplianceTaskData(response));
          }

          if (response?.action === 'asset_criticality') {
            eventEmitter.emit('asset_criticality_update', response);
          }

          // Bulk whitelist approve/reject job lifecycle (Kafka async)
          if (
            response?.action === "whitelist_approve_started" ||
            response?.code === "whitelist_approve_started" ||
            response?.action === "whitelist_approve_completed" ||
            response?.code === "whitelist_approve_completed" ||
            response?.action === "whitelist_approve_failed" ||
            response?.code === "whitelist_approve_failed"
          ) {
            eventEmitter.emit("whitelist_approve_job", response);
          }

          if (
            response?.action === 'scan_completed' || 
            response?.action === 'scan_initiated' || 
            response?.action === 'scan_started' || 
            response?.action === 'scan_failed' ||
            (response?.action && response.action.startsWith('scan_'))
          ) {
            // Unwrap nested data payloads; some events wrap the actual scan row in .data
            const payloadRows = Array.isArray(response.data)
              ? response.data.map((item: any) => ({ action: response.action, ...item }))
              : response.data && typeof response.data === 'object'
              ? [{ action: response.action, ...response.data }]
              : [response];

            console.log('✅ Scan status event detected on org topic! Routing to myscanSocketData', payloadRows);
            dispatch(setMyScanSocketData(payloadRows));
          }

          if (response && response.action &&
            NOTIFICATION_REASONS.includes(response.action)) {

            // Skip the refresh toast if it's a myscan asset_criticality update
            // because we handle it in real-time locally in Discovery.tsx
            if (response.action === 'asset_criticality' && response.scan_type === 'myscan') {
              console.log('Skipping toast for myscan asset_criticality (handled in real-time)');
              return;
            }

            // Get current logged-in user's ID from localStorage
            const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
            const currentUserId = userDetails?.usersid || '';

            // ===== DEBUG: Log currentUserId details =====
            console.log('👤 localStorage userDetails.usersid:', userDetails?.usersid, '| type:', typeof userDetails?.usersid);
            console.log('👤 localStorage userDetails.user_id:', userDetails?.user_id, '| type:', typeof userDetails?.user_id);
            console.log('👤 localStorage userDetails.userId:', userDetails?.userId, '| type:', typeof userDetails?.userId);
            console.log('👤 currentUserId (resolved):', currentUserId, '| type:', typeof currentUserId);
            // ============================================

            // Only show toast if the action was performed by a different user
            // Socket response uses 'user_id', localStorage uses 'usersid'
            const responseUserId = response.user_id || response.usersid || '';

            // ===== DEBUG: Log comparison details =====
            console.log('🔍 responseUserId (resolved):', responseUserId, '| type:', typeof responseUserId);
            console.log('🔍 Strict comparison (===):', responseUserId === currentUserId);
            console.log('🔍 String comparison:', String(responseUserId) === String(currentUserId));
            console.log('🔍 Loose comparison (==):', responseUserId == currentUserId);
            // ==========================================

            const isOwnAction = responseUserId && currentUserId && String(responseUserId) === String(currentUserId);

            // ===== DEBUG: Log final decision =====
            console.log('✅ isOwnAction result:', isOwnAction);
            console.log('✅ responseUserId truthy:', !!responseUserId);
            console.log('✅ currentUserId truthy:', !!currentUserId);
            if (response.action === 'role_updated' || response.action === 'role_deleted') {
              console.log('🚨 ROLE EVENT DETECTED:', response.action, '| isOwnAction:', isOwnAction);
              console.log('🚨 Will logout?', !isOwnAction);
            }
            // ======================================

            if (!isOwnAction) {
              // Handle role update/delete - logout and redirect to login
              if (response.action === 'role_updated' || response.action === 'role_deleted') {
                console.log('🚨 PROCEEDING WITH LOGOUT for role event:', response.action);
                const roleReason = response.action === 'role_updated' ? 'role_updated' : 'role_deleted';
                setCookie('roleLogoutReason', roleReason);
                (window as any).__roleLogoutInProgress = true;

                // Call logout (same as idle timeout) - invalidates session and redirects to login
                logout(false, false, false);
                return;
              }

              ConfirmationToast({
                message: getNotificationMessage(response.action),
                onConfirm: () => {
                  // Emit legacy event for existing pages ONLY after user confirms
                  eventEmitter.emit('filterSaved', response);
                  console.log(`📡 Emitting filterSaved for reason: ${response.action} from constants`);
                  window.location.reload();
                },
                onCancel: () => {
                  // Do nothing when user closes
                },
                confirmText: "Refresh",
              });
            } else {
              console.log(`🔕 Skipping toast/logout for own action: ${response.action} (responseUserId: ${responseUserId}, currentUserId: ${currentUserId})`);
            }

            // Invalidate TanStack Query cache based on reason
            // invalidateQueriesForReason(queryClient, response.action);
            // console.log(`🔄 Query invalidation triggered for reason: ${response.action}`);
          }
        });


        // Handle agent status broadcast events (pause/resume/uninstall/install)
        socketInstance.on('broadcast', (response: any) => {
          console.log('[Broadcast] Agent broadcast event received:', response);
          if (response?.action === 'agent_paused' || response?.action === 'agent_resumed' || response?.action === 'agent_uninstalled' || response?.action === 'agent_installed') {
            console.log(`[Broadcast] Agent ${response.action} - agent_id: ${response.agent_id}, status: ${response.status}`);
            eventEmitter.emit('agent_status_update', response);
          }
        });

        // Add notification event handler
        socketInstance.on('message', (notificationData: any) => {
          console.log('Received notification:', notificationData);
          dispatch(setNotifications(notificationData))
          // While bulk async approve/reject is running, hide toast cards.
          // Final summary is allowed after the page clears this flag on is_final.
          const suppressBulkApproveToasts =
            sessionStorage.getItem("suppress_whitelist_approve_notifs") === "1";
          const notifCode = String(
            notificationData?.notification_category_code ||
              notificationData?.code ||
              notificationData?.event_type_name ||
              ""
          ).toLowerCase();
          const isRiskNotif = [
            "risk_approved",
            "risk_rejected",
            "risk_extended",
            "risk_approval_pending",
            "accepted_risk",
            "risk_acceptance",
            "whitelist_request",
          ].some((code) => notifCode.includes(code));
          if (suppressBulkApproveToasts && isRiskNotif) {
            console.log("Suppressing risk notification toast during bulk approve job");
            return;
          }
          showNotification({
            notification_category_code: notificationData.notification_category_code,
            message: notificationData.message,
            event_type_name: notificationData.event_type_name
          });
        });

        // Handle reconnecting
        socketInstance.on("reconnect", (attemptNumber: number) => {
          console.log(
            `Socket reconnected successfully on attempt ${attemptNumber}`
          );
        });

        // Handle failed reconnection
        socketInstance.on("reconnect_failed", () => {
          console.error("Socket reconnection failed.");
        });
      }
    } else {
      console.error(
        "User is not logged in. Cannot establish socket connection."
      );
    }
  };

  const reconnectSocket = () => {
    setTimeout(() => {
      const token = getToken();
      if (token) {
        console.log("Attempting to reconnect...");
        const socketInstance: any = connectSocket(
          `${WEBSOCKET_URL}`,
          {
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 5000,
            timeout: 10000,
            token: token,
            device_code: DEVICE_CODE
          }
        );
        socketRef.current = socketInstance;

        socketInstance.on("connect", () => {
          console.log("Reconnected to WebSocket server successfully!");
        });

        socketInstance.on("connect_error", (error: any) => {
          console.error("Reconnection error:", error.message);
        });
      }
    }, 3000);
  };

  // Initialize socket connection when component mounts or user logs in
  useEffect(() => {
    if (isLoggedIn) {
      socketConnection();
    } else {
      closeSocketConnection();
    }
  }, [isLoggedIn]);

  // Clean up socket connection when component unmounts
  useEffect(() => {
    return () => {
      closeSocketConnection();
    };
  }, []);



  return null;
};

export default EventsManager;
